/**
 * poiAutoEnable.ts — the P2 auto-enable policy for the POI layer (T-222 r2).
 *
 * POI is opt-in / off by default (T-052) because its data is only fetched around an
 * activeLocation; enabling it on zoom alone shows an empty layer. Ricardo's P2 pick surfaces
 * POI automatically, but only when it will actually have something to show — the user is BOTH
 * past POI_MIN_ZOOM AND has an activeLocation — and only ONCE per session, and NEVER again
 * after the user has manually toggled POI off. This pure predicate holds that whole decision so
 * it is unit-testable; MapShell owns the session-scoped flags (sessionStorage) and the effect
 * that acts on a `true` result.
 */
export interface PoiAutoEnableInput {
  /** Current map zoom. */
  zoom: number
  /** POI_MIN_ZOOM — the layer's native minzoom gate. */
  minZoom: number
  /** Whether an activeLocation (search/pin) exists — i.e. there is POI data to show. */
  hasLocation: boolean
  /** Whether the POI layer is already visible (nothing to auto-enable). */
  showPOI: boolean
  /** Whether the one-time auto-enable has already fired this session. */
  alreadyAutoEnabled: boolean
  /** Whether the user has manually toggled POI off this session (never auto-re-enable). */
  manuallyDisabled: boolean
}

export function shouldAutoEnablePoi(input: PoiAutoEnableInput): boolean {
  const { zoom, minZoom, hasLocation, showPOI, alreadyAutoEnabled, manuallyDisabled } = input
  if (showPOI) return false // already on
  if (alreadyAutoEnabled) return false // fire at most once per session
  if (manuallyDisabled) return false // respect a manual toggle-off permanently
  return zoom >= minZoom && hasLocation
}
